import { Link } from "react-router";
import { Code } from "lucide-react";
import { badgeVariants, cn } from "@headerhawk/ui";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { formatReportCount } from "@/lib/hooks/use-usage";
import { SCRIPT_HASH_QUOTA_EXPLANATION } from "./usage-card";

/** What the Scripts view shows, on the plans that have it. */
export const SCRIPTS_VIEW_ON_TEAM =
  "Team adds the Scripts view: every script your pages load, with its hash, its origin, how many pages and reports it appeared in, and when it was last seen.";

interface ScriptHashNavEntryProps {
  count: number;
  onNavigate: () => void;
}

/**
 * The sidebar's Scripts entry on a plan without the Scripts view (task 264).
 *
 * Hash collection is not gated by plan and every hash report is charged to
 * quota, so hiding the entry would leave a Free site paying for reports it had
 * no sign of. It shows the period's count instead, and leads to billing, where
 * the usage card carries the same number.
 */
export function ScriptHashNavEntry({
  count,
  onNavigate,
}: ScriptHashNavEntryProps) {
  const formatted = formatReportCount(count);

  return (
    <div
      className="flex items-start gap-1 rounded-lg px-3 py-2 text-sm"
      data-testid="scripts-nav-entry-locked"
    >
      <Link
        to="/dashboard/settings/billing"
        onClick={onNavigate}
        className="flex flex-1 items-start gap-3 text-muted-foreground transition-colors hover:text-foreground"
      >
        <Code className="h-5 w-5 shrink-0" />
        <span className="flex flex-col gap-0.5">
          <span className="flex items-center gap-2 font-medium">
            Scripts
            <span className={cn(badgeVariants({ variant: "secondary" }))}>
              Team
            </span>
          </span>
          <span className="text-xs">{formatted} script hash reports</span>
        </span>
      </Link>
      <InfoTooltip
        content={`${SCRIPT_HASH_QUOTA_EXPLANATION} ${SCRIPTS_VIEW_ON_TEAM}`}
      />
    </div>
  );
}
